import Image from 'next/image';
import Container from 'components/container/container';
import ProductPage from 'components/product';
import {
  ProductDivider,
  ProductFeatureCollapsible,
  ProductFeatureContainer,
  ProductFeatureLayoutGrid,
  ProductFeatureLayoutGridNode,
  ProductFeatureRequest,
  ProductFeatureShowMoreContainer,
  ProductFeatureShowMoreLink,
  ProductGitHubActionsSection,
} from 'components/product/product.styles';
import { ProductFeatureStatus } from 'components/product/product.types';
import Link from 'next/link';
import { useState } from 'react';

import CheckmarkIcon from 'public/icons/eva/checkmark-circle.svg';
import SyncIcon from 'public/icons/eva/sync.svg';
import HoverArrow from 'public/icons/hover_arrow.svg';

import {
  GITHUB_ACTION_SYNCHRONIZE_SYMITAR_URL,
  GITHUB_ACTION_VALIDATE_POWERON_URL,
  LIBUM_FEEDBACK,
  LIBUM_URL,
  POWERON_PIPELINES_MARKETPLACE_URL,
} from 'shared/constants/globals';
import { PRODUCTS } from 'shared/data/products';
import { useTheme } from 'shared/styles/theme';

const POWERON_PIPELINES = PRODUCTS.find((p) => p.id === 'poweron-pipelines')!;

export default function PowerOnPipelines() {
  const { theme } = useTheme();
  const title = 'PowerOn Pipelines | Libum';
  const description =
    'PowerOn Pipelines brings true CI/CD capabilities to Symitar PowerOn development with automated validation and deployment for Azure DevOps and GitHub Actions.';
  const keywords =
    'PowerOn Pipelines,PowerOn CI/CD,PowerOn Azure DevOps,PowerOn GitHub Actions,PowerOn deployment,PowerOn validation,Symitar automation,PowerOn,Symitar,Symitar software,Jack Henry software,credit union software';
  const url = `${LIBUM_URL}/poweron-pipelines`;

  const [showMore, setShowMore] = useState(false);
  const handleShowMoreClick = () => {
    setShowMore(!showMore);
  };

  const getStatusDisplayName = (status: ProductFeatureStatus): string => {
    return status
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  };

  const productSchemaData = {
    name: 'PowerOn Pipelines',
    description: description,
    category: 'DeveloperApplication',
    operatingSystem: 'Windows, macOS, Linux',
    image: `${LIBUM_URL}/images/7f3e2d1c-8b4a-6e5d-9c7f-1a3b5e7d9c1f.png`,
  };

  return (
    <Container
      title={title}
      description={description}
      keywords={keywords}
      url={url}
      socialCard="poweron-pipelines"
      product={productSchemaData}
      breadcrumbs={[
        { name: 'Products', url: `${LIBUM_URL}/products` },
        { name: 'PowerOn Pipelines', url },
      ]}
    >
      <div style={{ width: '100%' }}>
        <ProductPage product={POWERON_PIPELINES}>
          <h1>PowerOn Pipelines</h1>
          <h2>Deploy, install, and validate your PowerOns within your pipelines.</h2>
          <ProductDivider />
          <p id="overview">
            In early 2025, we had a forward-thinking credit union reach out and ask for us to fill the version control
            gap for the Symitar core — a few months later, we published{' '}
            <Link href={POWERON_PIPELINES_MARKETPLACE_URL}>PowerOn Pipelines</Link> for Azure DevOps.
          </p>
          <p>
            With PowerOn Pipelines, your organization can maintain a central repository for your PowerOn files using
            your preferred CI/CD platform to validate changes during pull requests, orchestrate multi-stage deployments,
            and prevent invalid code from progressing — all while maintaining complete audit trails and role-based
            approvals.
          </p>
          <figure style={{ margin: '32px 0', padding: '0 48px' }}>
            <Image
              src={
                theme === 'dark'
                  ? '/images/7f3e2d1c-8b4a-6e5d-9c7f-1a3b5e7d9c1f.png'
                  : '/images/b2d4f6a8-1c3e-5a7b-9c1d-3e5f7a9b1c3d.png'
              }
              alt="PowerOn Pipelines in Azure DevOps"
              width={1160}
              height={714}
              style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
            />
            <figcaption style={{ marginTop: '12px', textAlign: 'center', fontSize: '14px', opacity: 0.8 }}>
              PowerOn Pipelines workflow for Azure DevOps in Visual Studio Code
            </figcaption>
          </figure>
          <ProductDivider />
          <ProductFeatureContainer id="features">
            <ProductFeatureLayoutGrid className="featured">
              {POWERON_PIPELINES.features
                .filter((f) => f.isFeatured)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={`isFeatured ${feature.docsLink ? 'has-link' : ''}`}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="image">
                      <Image
                        src={feature.imagePath}
                        alt={`${feature.name} feature screenshot`}
                        width={400}
                        height={250}
                        style={{ maxHeight: '100%', width: 'auto', height: 'auto' }}
                      />
                    </div>
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className={`details ${feature.offeringType}`}>
                        <div className="status">
                          <div className={`dot ${feature.status}`} />
                          {getStatusDisplayName(feature.status)}
                        </div>
                        <div className="offering">
                          <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                          {feature.offeringType}
                        </div>
                      </div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureLayoutGrid>
              {POWERON_PIPELINES.features
                .filter((f) => !f.isFeatured && !f.isHidden)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={feature.docsLink ? 'has-link' : ''}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className={`details ${feature.offeringType}`}>
                        <div className="status">
                          <div className={`dot ${feature.status}`} />
                          {getStatusDisplayName(feature.status)}
                        </div>
                        <div className="offering">
                          <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                          {feature.offeringType}
                        </div>
                      </div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureCollapsible className={showMore ? 'opened' : ''}>
              <div>
                <ProductFeatureLayoutGrid>
                  {POWERON_PIPELINES.features
                    .filter((f) => f.isHidden)
                    .map((feature) => (
                      <ProductFeatureLayoutGridNode
                        key={feature.id}
                        className={feature.docsLink ? 'has-link' : ''}
                        onClick={() => {
                          feature.docsLink && window.open(feature.docsLink, '_blank');
                        }}
                        id={feature.id}
                      >
                        <div className="content">
                          <div className="name">{feature.name}</div>
                          <div className="details">
                            <div className="status">
                              <div className={`dot ${feature.status}`} />
                              {getStatusDisplayName(feature.status)}
                            </div>
                            <div className="offering">
                              <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                              {feature.offeringType}
                            </div>
                          </div>
                          <div className="description">{feature.description}</div>
                        </div>
                      </ProductFeatureLayoutGridNode>
                    ))}
                </ProductFeatureLayoutGrid>
              </div>
            </ProductFeatureCollapsible>
          </ProductFeatureContainer>
          <ProductFeatureShowMoreContainer>
            <ProductFeatureShowMoreLink onClick={handleShowMoreClick}>
              {showMore ? 'Show less' : 'Show more'}
            </ProductFeatureShowMoreLink>
          </ProductFeatureShowMoreContainer>
          <ProductDivider />
          <ProductGitHubActionsSection id="github-actions">
            <h3>Have a GitHub organization instead?</h3>
            <p>Use our verified GitHub Actions directly in your workflows — no Azure DevOps required.</p>
            <div className="actions-grid">
              <div className="action-card">
                <div className="action-name">
                  <CheckmarkIcon />
                  Validate PowerOn
                </div>
                <div className="action-description">
                  Validate PowerOn files during pull requests and builds to prevent invalid code from merging into your
                  repository.
                </div>
                <Link href={GITHUB_ACTION_VALIDATE_POWERON_URL} target="_blank">
                  View on GitHub Marketplace
                  <HoverArrow />
                </Link>
              </div>
              <div className="action-card">
                <div className="action-name">
                  <SyncIcon />
                  Synchronize Symitar
                </div>
                <div className="action-description">
                  Automatically sync PowerOn files, letter files, data files, and help files from your repository to
                  Symitar environments.
                </div>
                <Link href={GITHUB_ACTION_SYNCHRONIZE_SYMITAR_URL} target="_blank">
                  View on GitHub Marketplace
                  <HoverArrow />
                </Link>
              </div>
            </div>
          </ProductGitHubActionsSection>
          <ProductDivider />
          <ProductFeatureRequest>
            <h3>Have a feature you&apos;d like to see in PowerOn Pipelines?</h3>
            <Link href={LIBUM_FEEDBACK} target="_blank">
              Submit Your Idea
            </Link>
          </ProductFeatureRequest>
        </ProductPage>
      </div>
    </Container>
  );
}
